import find from "lodash/find";
import map from "lodash/map";
import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import useCustomizationContext from "../../../../hooks/useCustomizationContext";
import { ReactComponent as GoogleIcon } from "../../../../icons/google.svg";
import { ReactComponent as IconPlus } from "../../../../icons/plus.svg";
import BlankSelectionProvider from "../../../../providers/selection/BlankSelectionProvider";
import FacebookSelectionProvider from "../../../../providers/selection/FacebookSelectionProvider";
import GoogleSelectionProvider from "../../../../providers/selection/GoogleSelectionProvider";
import InstagramSelectionProvider from "../../../../providers/selection/InstagramSelectionProvider";
import MobileUploadSelectionProvider from "../../../../providers/selection/MobileUploadSelectionProvider";
import UnsplashSelectionProvider from "../../../../providers/selection/UnsplashSelectionProvider";
import UploadSelectionProvider from "../../../../providers/selection/UploadSelectionProvider";
import { SelectionProviderToken } from "../../../../types/customization/configuration";
import { SelectionPhoto, SelectionProvider } from "../../../../types/selection/selection";
import { maxPageReached } from "../../../../utils/customization/configuration";
import { getNoCoverDefinitions } from "../../../../utils/customization/cover";
import { findPagesWithEmptyEditablePictures } from "../../../../utils/customization/finders";
import { handleFile } from "../../../../utils/selection/upload";
import Button from "../../../atoms/Button";
import SelectionUploadButton from "../../../atoms/selection/SelectionUploadButton";
import SelectionHeaderProvider from "../../../molecules/selection/SelectionHeaderProvider";
import SelectionUploadSummary from "../../../molecules/selection/SelectionUploadSummary";
import SelectionProviderBrowser from "../SelectionProviderBrowser";
import "./style.scss";

const initialProvider = process.env.NODE_ENV === "development" ? "unsplash" : "upload";

const Selection: FunctionComponent<{ pageKey?: string }> = ({ pageKey }) => {
  const {
    configuration,
    content,
    dispatch,
    selection: { photos: selectionPhotos },
    template,
    ui: { editedPicture },
  } = useCustomizationContext();
  const [uploadVersion, setUploadVersion] = useState<number>(0);
  const [currentProvider, setCurrentProvider] = useState<SelectionProvider>(initialProvider);
  const [hasDataCurrentProvider, setHasDataCurrentProvider] = useState<boolean>(false);
  const [hasOpenedAlbum, setHasOpenedAlbum] = useState<boolean>(false);

  const handleChangeCurrentProvider = (provider: SelectionProvider) => {
    setCurrentProvider(provider);
    setHasOpenedAlbum(false);
  };

  const selectionProviderConnect = configuration
    ? configuration.selectionProviderConnect
    : undefined;
  const selectionProviderDisconnect = configuration
    ? configuration.selectionProviderDisconnect
    : undefined;
  const uploadProvider = configuration ? configuration.uploadProvider : undefined;

  const selectionProviderTokens: SelectionProviderToken[] = useMemo(() => {
    if (!configuration || !configuration.selectionProviderTokens) {
      return [];
    }
    return configuration.selectionProviderTokens;
  }, [configuration]);

  const facebookSelectionProvider = useMemo(() => {
    const token = selectionProviderTokenFor("facebook", selectionProviderTokens);
    return token ? new FacebookSelectionProvider(token) : new BlankSelectionProvider();
  }, [selectionProviderTokens]);
  const instagramSelectionProvider = useMemo(() => {
    const token = selectionProviderTokenFor("instagram", selectionProviderTokens);
    return token ? new InstagramSelectionProvider(token) : new BlankSelectionProvider();
  }, [selectionProviderTokens]);
  const googleSelectionProvider = useMemo(() => {
    const token = selectionProviderTokenFor("google_photos", selectionProviderTokens);
    return token ? new GoogleSelectionProvider(token) : new BlankSelectionProvider();
  }, [selectionProviderTokens]);
  const mobileUploadSelectionProvider = useMemo(() => {
    const token = selectionProviderTokenFor("mobile_upload", selectionProviderTokens);
    return token ? new MobileUploadSelectionProvider() : new BlankSelectionProvider();
  }, [selectionProviderTokens]);
  const unsplashSelectionProvider = useMemo(() => {
    return new UnsplashSelectionProvider();
  }, []);
  const uploadSelectionProvider = useMemo(() => {
    const token = selectionProviderTokenFor("upload", selectionProviderTokens);
    return token ? new UploadSelectionProvider() : new BlankSelectionProvider();
  }, [selectionProviderTokens]);

  useEffect(() => {
    if (!uploadProvider) {
      return;
    }

    return uploadProvider.onQueueEmpty(() => {
      dispatch({
        code: "KFV-002",
        type: "addNotificationByCode",
      });

      uploadSelectionProvider.refreshAlbums();
      uploadSelectionProvider.refreshAlbumPhotos();
      setHasDataCurrentProvider(true);

      setUploadVersion((prevUploadVersion) => prevUploadVersion + 1);
    });
  }, [dispatch, setUploadVersion, uploadProvider, uploadSelectionProvider]);

  const onFileChange = useCallback(
    (files: File[]) => {
      // to deactivate easily if needed
      const isReorderedByDate = true;
      const uploadedFiles: SelectionPhoto[] = [];

      if (!uploadProvider) {
        return Promise.resolve();
      }

      const addCurrentPicture = (selectionPhoto: SelectionPhoto) => {
        dispatch({
          avoidCover: true,
          configuration,
          selectionPhoto,
          tryToFillFirstEmptyPicture: true,
          type: "addPicture",
          undoKey: "autofill",
        });
      };

      const autoFillByDate = (selectedFiles: SelectionPhoto[]) => {
        if (isReorderedByDate && selectedFiles.length > 0) {
          const orderedFiles = selectedFiles.sort((fileA, fileB) => {
            if (!fileA.lastModified) {
              fileA.lastModified = Date.now();
            }
            if (!fileB.lastModified) {
              fileB.lastModified = Date.now();
            }
            return fileA.lastModified - fileB.lastModified;
          });
          orderedFiles.forEach((file) => {
            addCurrentPicture(file);
          });
        }
      };

      return Promise.all(
        map(files, (file, index) => {
          return handleFile(file, dispatch, uploadProvider).then((selectionPhoto) => {
            if (
              content.pages.length === 1 &&
              findPagesWithEmptyEditablePictures(content.pages, template).length === 0
            ) {
              return;
            }
            const noCoverDefinitions = getNoCoverDefinitions(template.pageDefinitions);
            if (maxPageReached(content, configuration, noCoverDefinitions) || !selectionPhoto) {
              return;
            }
            if (pageKey && editedPicture && index === 0) {
              dispatch({
                configuration,
                editablePictureKey: editedPicture.editablePictureKey,
                pageKey,
                selectionPhoto,
                tryToFillFirstEmptyPicture: false,
                type: "addPicture",
                undoKey: `PictureEditorView-${pageKey}`,
              });
            } else if (isReorderedByDate) {
              uploadedFiles.push(selectionPhoto);
            } else {
              addCurrentPicture(selectionPhoto);
            }

            if (uploadedFiles.length === files.length) {
              autoFillByDate(uploadedFiles);
            }
          });
        })
      ).then(() => Promise.resolve());
    },
    [configuration, content, dispatch, editedPicture, pageKey, template, uploadProvider]
  );

  const selectionUploadButtonElement = uploadProvider ? (
    <SelectionUploadButton onFileChange={onFileChange}>
      <IconPlus height="16" width="16" />
      <FormattedMessage id="kustom_Selection_footer_buttonLabel_upload" />
    </SelectionUploadButton>
  ) : null;

  const checkDataFromCurrentProvider = (hasData: boolean) => {
    const selectionFromProvider = selectionPhotos.filter(
      (photo) => photo.provider === currentProvider
    );
    setHasDataCurrentProvider(hasData || selectionFromProvider.length > 0);
  };

  const handleAlbumOpened = (opened: string | null) => {
    setHasOpenedAlbum(opened !== null);
  };

  return (
    <div className="Selection">
      <div className="Selection-header">
        {process.env.NODE_ENV === "development" ? (
          <SelectionHeaderProvider
            onClick={handleChangeCurrentProvider}
            provider="unsplash"
            selected={currentProvider === "unsplash"}
          />
        ) : null}
        <SelectionHeaderProvider
          onClick={handleChangeCurrentProvider}
          provider="upload"
          selected={currentProvider === "upload"}
        />
        <SelectionHeaderProvider
          onClick={handleChangeCurrentProvider}
          provider="facebook"
          selected={currentProvider === "facebook"}
        />
        <SelectionHeaderProvider
          onClick={handleChangeCurrentProvider}
          provider="google_photos"
          selected={currentProvider === "google_photos"}
        />
        <SelectionHeaderProvider
          onClick={handleChangeCurrentProvider}
          provider="instagram"
          selected={currentProvider === "instagram"}
        />
        <SelectionHeaderProvider
          onClick={handleChangeCurrentProvider}
          provider="mobile_upload"
          selected={currentProvider === "mobile_upload"}
        />
      </div>

      {currentProvider === "facebook" &&
      facebookSelectionProvider.isBlank() &&
      selectionProviderConnect ? (
        <div className="Selection-content-add">
          <div className="Selection-content-add-intro">
            <FormattedMessage id="kustom_Selection_footer_intro_facebook" />
          </div>
          <div className="Selection-content-add-button">
            <Button onClick={() => selectionProviderConnect("facebook")}>
              <FormattedMessage id="kustom_Selection_footer_buttonLabel_facebook" />
            </Button>
          </div>
        </div>
      ) : null}
      {currentProvider === "google_photos" &&
      googleSelectionProvider.isBlank() &&
      selectionProviderConnect ? (
        <div className="Selection-content-add Selection-content-add-google">
          <div className="Selection-content-add-intro">
            <FormattedMessage id="kustom_Selection_footer_intro_google" />
          </div>
          <div className="Selection-content-add-description">
            <FormattedMessage id="kustom_Selection_footer_description_google" />
          </div>
          <button
            className="Selection-content-add-button-google"
            onClick={() => selectionProviderConnect("google_photos")}
          >
            <GoogleIcon />
            <FormattedMessage id="kustom_Selection_footer_buttonLabel_google" />
          </button>
        </div>
      ) : null}
      {currentProvider === "instagram" &&
      instagramSelectionProvider.isBlank() &&
      selectionProviderConnect ? (
        <div className="Selection-content-add">
          <div className="Selection-content-add-intro">
            <FormattedMessage id="kustom_Selection_footer_intro_instagram" />
          </div>
          <div className="Selection-content-add-button">
            <Button onClick={() => selectionProviderConnect("instagram")}>
              <FormattedMessage id="kustom_Selection_footer_buttonLabel_instagram" />
            </Button>
          </div>
        </div>
      ) : null}
      {currentProvider === "upload" && hasOpenedAlbum === false ? (
        <div className="Selection-content-add">
          <div className="Selection-content-add-intro">
            <FormattedMessage id="kustom_Selection_add_intro_upload" />
          </div>
          <div className="Selection-content-add-button">{selectionUploadButtonElement}</div>
        </div>
      ) : null}
      {currentProvider === "mobile_upload" ? (
        <div className="Selection-content-add">
          <div className="Selection-content-add-intro">
            <FormattedMessage id="kustom_Selection_footer_intro_mobileUpload" />
          </div>
          <div className="Selection-content-add-button">
            <Button>
              <FormattedMessage id="kustom_Selection_footer_buttonLabel_mobileUpload" />
            </Button>
          </div>
          {hasDataCurrentProvider === false && (
            <div className="Selection-content-add-tuto">
              <ol className="Selection-content-add-tuto-list">
                <li>
                  <FormattedMessage id="kustom_Selection_footer_tuto_mobile_step1" />
                </li>
                <li>
                  <FormattedMessage id="kustom_Selection_footer_tuto_mobile_step2" />
                </li>
                <li>
                  <FormattedMessage
                    id="kustom_Selection_footer_tuto_mobile_step3"
                    values={{
                      strong: (...chunks: string[]) => <strong>{chunks}</strong>,
                    }}
                  />
                </li>
              </ol>
              <p className="Selection-content-add-tuto-big">
                <FormattedMessage id="kustom_Selection_footer_tuto_mobile_end" />
              </p>
            </div>
          )}
        </div>
      ) : null}

      <div className="Selection-content">
        <div className="Selection-content-main">
          {currentProvider === "facebook" && facebookSelectionProvider ? (
            <SelectionProviderBrowser
              pageKey={pageKey}
              provider="facebook"
              providerInstance={facebookSelectionProvider}
              selectionPhotos={selectionPhotos}
              uploadProvider={uploadProvider}
            />
          ) : null}
          {currentProvider === "google_photos" && googleSelectionProvider ? (
            <SelectionProviderBrowser
              pageKey={pageKey}
              provider="google_photos"
              providerInstance={googleSelectionProvider}
              selectionPhotos={selectionPhotos}
              uploadProvider={uploadProvider}
              selectionProviderConnect={selectionProviderConnect}
              selectionProviderDisconnect={selectionProviderDisconnect}
            />
          ) : null}
          {currentProvider === "instagram" && instagramSelectionProvider ? (
            <SelectionProviderBrowser
              pageKey={pageKey}
              provider="instagram"
              providerInstance={instagramSelectionProvider}
              selectionPhotos={selectionPhotos}
              uploadProvider={uploadProvider}
            />
          ) : null}
          {currentProvider === "mobile_upload" && mobileUploadSelectionProvider ? (
            <SelectionProviderBrowser
              pageKey={pageKey}
              provider="mobile_upload"
              providerInstance={mobileUploadSelectionProvider}
              selectionPhotos={selectionPhotos}
            />
          ) : null}
          {currentProvider === "unsplash" && unsplashSelectionProvider ? (
            <SelectionProviderBrowser
              pageKey={pageKey}
              provider="unsplash"
              providerInstance={unsplashSelectionProvider}
              selectionPhotos={selectionPhotos}
              uploadProvider={uploadProvider}
            />
          ) : null}
          {currentProvider === "upload" && uploadSelectionProvider ? (
            <SelectionProviderBrowser
              onAlbumOpened={handleAlbumOpened}
              onDataFetch={checkDataFromCurrentProvider}
              pageKey={pageKey}
              provider="upload"
              providerInstance={uploadSelectionProvider}
              selectionPhotos={selectionPhotos}
              uploadVersion={uploadVersion}
            />
          ) : null}
        </div>
      </div>

      {uploadProvider ? (
        <div className="Selection-uploadSummary">
          <SelectionUploadSummary uploadProvider={uploadProvider} />
        </div>
      ) : null}
    </div>
  );
};

export default Selection;

const selectionProviderTokenFor = (
  provider: SelectionProvider,
  selectionProviderTokens: SelectionProviderToken[]
): string | undefined => {
  if (provider === "instagram") {
    const instagramSelectionProviderToken = localStorage.getItem("instagram");
    return instagramSelectionProviderToken ? instagramSelectionProviderToken : undefined;
  } else {
    const selectionProviderToken = find(selectionProviderTokens, (s) => s.provider === provider);

    if (!selectionProviderToken) {
      return;
    }

    return selectionProviderToken.token;
  }
};
